'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { BodyShort, CopyButton, Heading, HStack, Link, Select } from '@navikt/ds-react';
import styles from './ResultatKortliste.module.css';
import { ArbeidsstedResponse, ArbeidstakerResponse } from '@/api/aaregDistApi';
import { store } from '@/app/_components/store/store';
import { formatDate } from '@/utils/formatting/dato';
import { formatFnrOrgnr } from '@/utils/formatting/tall';
import { lagKopiAriaLabel } from '@/utils/formatting/tekst';
type Arbeidsforhold = ArbeidsstedResponse['arbeidsforhold'][0] | ArbeidstakerResponse['arbeidsforhold'][0];
type Sortering = 'navn' | 'startdato' | 'sluttdato' | 'stillingsprosent' | 'yrke';
const sorter = (data: Arbeidsforhold[], sortering: Sortering, hentNavn: (data: Arbeidsforhold) => string) => {
  const sortert = [...data];
  switch (sortering) {
    case 'navn':
      return sortert.sort((a, b) => hentNavn(a).localeCompare(hentNavn(b), 'nb'));
    case 'startdato':
      return sortert.sort((a, b) => (b.ansettelsesperiodeStart || '').localeCompare(a.ansettelsesperiodeStart || ''));
    case 'sluttdato':
      return sortert.sort((a, b) => (b.ansettelsesperiodeSlutt || '').localeCompare(a.ansettelsesperiodeSlutt || ''));
    case 'stillingsprosent':
      return sortert.sort((a, b) => Number(b.gjeldendeAnsettelsesdetaljer?.avtaltStillingsprosent ?? 0) - Number(a.gjeldendeAnsettelsesdetaljer?.avtaltStillingsprosent ?? 0));
    case 'yrke':
      return sortert.sort((a, b) => (a.gjeldendeAnsettelsesdetaljer?.yrke?.beskrivelse || '').localeCompare(b.gjeldendeAnsettelsesdetaljer?.yrke?.beskrivelse || '', 'nb'));
  }
};
export default function ResultatKortliste(props) {
  const $ = _c(34);
  const {
    tekst
  } = store.useReducerState();
  const [sortering, setSortering] = React.useState("startdato");
  let t0;
  let t1;
  let t2;
  if ($[0] !== props || $[1] !== sortering || $[2] !== tekst) {
    const sortert = sorter(props.arbeidsforhold, sortering, props.hentNavn);
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = e => setSortering(e.target.value as Sortering);
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== tekst.filter.sorterNavn) {
      t4 = <option value="navn">{tekst.filter.sorterNavn}</option>;
      $[7] = tekst.filter.sorterNavn;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== tekst.filter.sorterStartdato) {
      t5 = <option value="startdato">{tekst.filter.sorterStartdato}</option>;
      $[9] = tekst.filter.sorterStartdato;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== tekst.filter.sorterSluttdato) {
      t6 = <option value="sluttdato">{tekst.filter.sorterSluttdato}</option>;
      $[11] = tekst.filter.sorterSluttdato;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== tekst.filter.sorterStillingsprosent) {
      t7 = <option value="stillingsprosent">{tekst.filter.sorterStillingsprosent}</option>;
      $[13] = tekst.filter.sorterStillingsprosent;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== tekst.filter.sorterYrke) {
      t8 = <option value="yrke">{tekst.filter.sorterYrke}</option>;
      $[15] = tekst.filter.sorterYrke;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    if ($[17] !== sortering || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== tekst.filter.sorterEtter) {
      t2 = <Select className={styles.sortering} label={tekst.filter.sorterEtter} size="small" value={sortering} onChange={t3}>{t4}{t5}{t6}{t7}{t8}</Select>;
      $[17] = sortering;
      $[18] = t4;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
      $[22] = t8;
      $[23] = tekst.filter.sorterEtter;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    t0 = styles.liste;
    let t9;
    if ($[25] !== props || $[26] !== tekst) {
      t9 = arbeidsforhold => {
        const navn = props.hentNavn(arbeidsforhold);
        return <li key={arbeidsforhold.uuid} className={styles.kort}><Heading level="3" size="xsmall"><Link as="button" type="button" className={styles.lenke} aria-label={"\xC5pne arbeidsforhold for " + navn} onClick={() => {
              props.beforeNavigateToArbeidsforhold?.();
              window.sessionStorage.setItem("arbeidsforholdUUID", JSON.stringify(arbeidsforhold.uuid));
              props.onNavigateToArbeidsforhold?.(arbeidsforhold.uuid);
            }}>{navn}</Link></Heading><dl className={styles.detaljer}><BodyShort as="dt" size="small">{props.identLabel}</BodyShort><BodyShort as="dd" size="small"><HStack gap="space-4" align="center">{formatFnrOrgnr(arbeidsforhold[props.soeker].offentligIdent)}<span className={styles.kopier}><CopyButton data-color="accent" size="xsmall" copyText={arbeidsforhold[props.soeker].offentligIdent} aria-label={lagKopiAriaLabel(arbeidsforhold[props.soeker].offentligIdent, tekst, navn)} /></span></HStack></BodyShort><BodyShort as="dt" size="small">{tekst.term.startdato}</BodyShort><BodyShort as="dd" size="small">{formatDate(arbeidsforhold.ansettelsesperiodeStart)}</BodyShort><BodyShort as="dt" size="small">{tekst.term.sluttdato}</BodyShort><BodyShort as="dd" size="small">{formatDate(arbeidsforhold.ansettelsesperiodeSlutt)}</BodyShort><BodyShort as="dt" size="small">{tekst.term.stillingsprosent}</BodyShort><BodyShort as="dd" size="small">{Number(arbeidsforhold.gjeldendeAnsettelsesdetaljer.avtaltStillingsprosent).toFixed(2) + " %"}</BodyShort><BodyShort as="dt" size="small">{tekst.term.yrke}</BodyShort><BodyShort as="dd" size="small">{arbeidsforhold.gjeldendeAnsettelsesdetaljer?.yrke?.beskrivelse}</BodyShort></dl></li>;
      };
      $[25] = props;
      $[26] = tekst;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    t1 = sortert.map(t9);
    $[0] = props;
    $[1] = sortering;
    $[2] = tekst;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[28] !== t0 || $[29] !== t1) {
    t3 = <ul className={t0}>{t1}</ul>;
    $[28] = t0;
    $[29] = t1;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== t2 || $[32] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
}